/**
 * CUSTOMER-R4.5 — reusable compact bank requisites (checkout / order-created / order detail).
 * Copy is per-field. Does not change payment state.
 */
import { useState } from "react";
import { copyText } from "./contact-manager-v2.js";
import { emitEvent } from "./events-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";
import { STORE_INFO } from "./store-merchant-sot-v2.js";
import { getBankPaymentPurpose } from "./bank-iban-payment-v2.js";

export type BankPaymentDetailsFields = {
  amountLabel: string;
  purpose?: string | null;
  seller?: string;
  rnokpp?: string;
  bank?: string;
  iban?: string;
  ibanDisplay?: string;
  publicOrderNumber?: string | null;
};

export function merchantBankDetails(
  amountLabel: string,
  purpose?: string | null,
  publicOrderNumber?: string | null,
): BankPaymentDetailsFields {
  return {
    amountLabel,
    purpose: purpose ?? null,
    publicOrderNumber: publicOrderNumber ?? null,
    seller: STORE_INFO.merchantName,
    rnokpp: STORE_INFO.edrpou,
    bank: STORE_INFO.bank,
    iban: STORE_INFO.iban,
    ibanDisplay: STORE_INFO.ibanDisplay,
  };
}

export function bankDetailsCopyAll(fields: BankPaymentDetailsFields): string {
  const purpose =
    fields.purpose?.trim() ||
    getBankPaymentPurpose(fields.publicOrderNumber) ||
    null;
  const lines = [
    fields.publicOrderNumber ? `Замовлення: ${fields.publicOrderNumber}` : null,
    `Сума: ${fields.amountLabel}`,
    `Отримувач: ${fields.seller ?? STORE_INFO.merchantName}`,
    `РНОКПП: ${fields.rnokpp ?? STORE_INFO.edrpou}`,
    `Банк: ${fields.bank ?? STORE_INFO.bank}`,
    `IBAN: ${fields.ibanDisplay ?? fields.iban ?? STORE_INFO.ibanDisplay}`,
    purpose ? `Призначення платежу: ${purpose}` : null,
  ].filter(Boolean);
  return lines.join("\n");
}

export function BankPaymentDetailsV2({
  lang,
  fields,
  showCopyAll = true,
  testId = "sv2-bank-details",
}: {
  lang: StoreV2Lang;
  fields: BankPaymentDetailsFields;
  showCopyAll?: boolean;
  testId?: string;
}) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [manual, setManual] = useState<string | null>(null);
  const copiedLabel = t(lang, "copied_short");
  const copyAllDone = t(lang, "copy_all_done");
  const copyLabel = lang === "en" ? "Copy" : "Скопіювати";
  const seller = fields.seller ?? STORE_INFO.merchantName;
  const rnokpp = fields.rnokpp ?? STORE_INFO.edrpou;
  const bank = fields.bank ?? STORE_INFO.bank;
  const iban = fields.iban ?? STORE_INFO.iban;
  const ibanDisplay = fields.ibanDisplay ?? STORE_INFO.ibanDisplay;

  const flash = (k: string) => {
    setCopiedKey(k);
    setManual(null);
    window.setTimeout(() => setCopiedKey(null), 1600);
  };
  const copy = async (k: string, v: string) => {
    if (k === "all") {
      emitEvent("bank_requisites_copy_all", {
        public_order_number: fields.publicOrderNumber ?? null,
      });
    } else {
      emitEvent("bank_field_copied", {
        field: k,
        public_order_number: fields.publicOrderNumber ?? null,
      });
    }
    if (await copyText(v)) flash(k);
    else setManual(v);
  };

  const Row = ({
    label,
    value,
    k,
    copyValue,
  }: {
    label: string;
    value: string;
    k: string;
    copyValue?: string;
  }) => (
    <div className="sv2-fop-row" data-testid={`${testId}-row-${k}`}>
      <div className="sv2-fop-row-text">
        <span className="sv2-fop-label">{label}</span>
        <span className="sv2-fop-value">{value}</span>
      </div>
      <button
        type="button"
        className="sv2-copy-btn sv2-copy-inline"
        data-testid={`${testId}-copy-${k}`}
        onClick={() => void copy(k, copyValue ?? value)}
      >
        {copiedKey === k ? copiedLabel : copyLabel}
      </button>
    </div>
  );

  // CUSTOMER-R4.15-C — purpose lives in BANK requisites (not checkout order header).
  // Order: recipient → tax id → bank → IBAN → purpose; each field has its own Copy.
  return (
    <div className="sv2-info-card sv2-fop-compact sv2-bank-details" data-testid={testId} data-purpose-in-requisites="1">
      <Row label={t(lang, "amount_to_pay")} value={fields.amountLabel} k="amount" />
      <Row label={lang === "en" ? "Recipient" : "Отримувач"} value={seller} k="seller" />
      <Row label="РНОКПП" value={rnokpp} k="rnokpp" />
      <Row label={lang === "en" ? "Bank" : "Банк"} value={bank} k="bank" />
      <Row label="IBAN" value={ibanDisplay} k="iban" copyValue={iban} />
      <Row
        label={lang === "en" ? "Payment purpose" : "Призначення платежу"}
        value={
          fields.purpose?.trim() ||
          (lang === "en" ? "Payment for order STG-…" : "Оплата за замовлення STG-…")
        }
        k="purpose"
      />
      {showCopyAll ? (
        <button
          type="button"
          className={`sv2-cta sv2-bank-copy-all-cta${copiedKey === "all" ? " is-copied" : ""}`}
          data-testid={`${testId}-copy-all`}
          onClick={() => void copy("all", bankDetailsCopyAll(fields))}
        >
          <span aria-hidden="true">{copiedKey === "all" ? "✓" : "📋"}</span>
          {copiedKey === "all" ? copyAllDone : t(lang, "copy_all")}
        </button>
      ) : null}
      {manual ? (
        <textarea className="sv2-manual-copy" readOnly value={manual} onFocus={(e) => e.target.select()} />
      ) : null}
    </div>
  );
}
